import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { BulkPutAggregateBusinessRequestUpdateInfos } from '@/coreInfrastructureModule/axios/generated/aggregateBusinessManage';
import {
  BulkPutBaseBusinessRequestUpdateInfos,
  GetBaseBusinessSuccessResponseInfo,
} from '@/coreInfrastructureModule/axios/generated/baseBusinessManage';
import { GetInventorySuccessResponseInfo } from '@/coreInfrastructureModule/axios/generated/inventoryManage';

/**
 * 取引編集のadapter
 */
export class BulkEditBusinessAdapter {
  /**
   * 集計一覧をレスポンスからUIタイプに変換する
   * @param response - レスポンス
   */
  public static adaptBulkAggregateBusiness(response: GetInventorySuccessResponseInfo): {
    businesses: IBusiness[];
    pagingOptions: IPagingOptions;
  } {
    const businesses =
      response.data?.map((d) => {
        let userUniqueNumber: string | undefined;
        try {
          if (typeof d.userCustomParameters === 'string' && d.userCustomParameters) {
            const params = JSON.parse(d.userCustomParameters);
            userUniqueNumber = params?.['顧客台帳ID'];
          }
        } catch (e) {
          console.error('Failed to parse userCustomParameters for aggregateBusinessId:', d.aggregateBusinessId, e);
        }
        return BusinessFactory.create({
          ...d,
          businessId: d.aggregateBusinessId,
          package: d.package?.toString(),
          MOQ: d.moq ?? undefined,
          stdInventoryIndices: {
            標準在庫指数: {
              min: d.standardInventoryIndexMin ?? 0,
              max: d.standardInventoryIndexMax ?? 0,
              option: { unit: d.standardInventoryIndexUnit ?? 'day' },
            },
          },
          purchaseUnitPrice: d.purchaseUnitPrice ? Number(d.purchaseUnitPrice) : undefined,
          userUniqueNumber: userUniqueNumber ?? undefined,
          executionDate: DateService.convertMSUnixTimeToDate(d.executionDate),
          aggregateBusinessResponsibleUsers: d.aggregateBusinessResponsibleUsers?.map((u) =>
            UserAccountFactory.create({ email: u.id, name: u.name })
          ),
          note: d.aggregateBusinessNote,
          arrivals: [],
          salesResults: [],
          createdAt: d.createAt,
          updatedAt: d.updateAt,
          createAt: { year: -1, month: -1, day: -1 },
          businessFreeColumnValues: BulkEditBusinessAdapter.toBusinessFreeColumnValues(d.businessFreeColumnValues),
        });
      }) ?? [];
    const pagingOptions = response.pagingOptions ?? initPagingOptions(0);

    return { businesses, pagingOptions };
  }

  /**
   * 集計一覧をレスポンスからUIタイプに変換する
   * @param response - レスポンス
   */
  public static adaptBulkBaseBusiness(response: GetBaseBusinessSuccessResponseInfo): {
    businesses: IBusiness[];
    pagingOptions: IPagingOptions;
  } {
    const businesses =
      response.data?.map((d) =>
        BusinessFactory.create({
          ...d,
          businessId: d.baseBusinessId,
          MOQ: d.moq != null ? Number(d.moq) : undefined,
          stdInventoryIndices: {
            標準在庫指数: {
              min: Number(d.standardInventoryIndexMin ?? 0),
              max: Number(d.standardInventoryIndexMax ?? 0),
              option: { unit: d.standardInventoryIndexUnit ?? 'day' },
            },
          },
          salesUnitPrice: d.salesUnitPrice != null ? Number(d.salesUnitPrice) : undefined,
          note: d.baseBusinessNote,
          baseBusinessResponsibleUsers: d.baseBusinessResponsibleUsers?.map((u) =>
            UserAccountFactory.create({ email: u.id, name: u.name })
          ),
          createdAt: d.createAt,
          updatedAt: d.updateAt,
          createAt: { year: -1, month: -1, day: -1 },
          businessFreeColumnValues: BulkEditBusinessAdapter.toBusinessFreeColumnValues(d.businessFreeColumnValues),
        })
      ) ?? [];
    const pagingOptions = response.pagingOptions ?? initPagingOptions(0);

    return { businesses, pagingOptions };
  }

  /**
   * 編集情報をリクエストの型に変換する
   * @param infos - リクエスト
   */
  public static adaptBulkPutAggregateBusinessRequestUpdateInfo(infos: Partial<IBusiness>): BulkPutAggregateBusinessRequestUpdateInfos {
    const isStdInvIndexUpdatable =
      !!infos.stdInventoryIndices?.['標準在庫指数'].min &&
      !!infos.stdInventoryIndices?.['標準在庫指数'].max &&
      !!infos.stdInventoryIndices?.['標準在庫指数'].option.unit;
    return {
      warehouse: infos.warehouse || undefined,
      warehouseNumber: infos.warehouseNumber || undefined,
      supplierName: infos.supplierName || undefined,
      supplierNumber: infos.supplierNumber || undefined,
      businessName: infos.businessName || undefined,
      businessNumber: infos.businessNumber || undefined,
      package: infos.package ? infos.package.toString() : undefined,
      unit: infos.unit || undefined,
      manufacturer: infos.manufacturer || undefined,
      moq: infos.MOQ ? infos.MOQ.toString() : undefined,
      stdInventoryIndexMin: isStdInvIndexUpdatable ? infos.stdInventoryIndices?.['標準在庫指数'].min.toString() : undefined,
      stdInventoryIndexMax: isStdInvIndexUpdatable ? infos.stdInventoryIndices?.['標準在庫指数'].max.toString() : undefined,
      stdInventoryIndexUnit: isStdInvIndexUpdatable ? infos.stdInventoryIndices?.['標準在庫指数'].option.unit : undefined,
      leadTime: !!infos.leadTime && !!infos.leadTimeUnit ? infos.leadTime.toString() : undefined,
      leadTimeUnit: !!infos.leadTime && !!infos.leadTimeUnit ? infos.leadTimeUnit : undefined,
      orderPackage: infos.orderPackage ? infos.orderPackage.toString() : undefined,
      purchaseUnitPrice: infos.purchaseUnitPrice ? infos.purchaseUnitPrice.toString() : undefined,
      aggregateBusinessResponsibleUserIdList:
        infos.aggregateBusinessResponsibleUsers && infos.aggregateBusinessResponsibleUsers.length > 0
          ? infos.aggregateBusinessResponsibleUsers?.map((u) => u.email).filter((uId) => uId != null)
          : undefined,
      userUniqueNumber: infos.userUniqueNumber,
      businessFreeColumnValues: BulkEditBusinessAdapter.toRequestBusinessFreeColumnValues(infos.businessFreeColumnValues),
    };
  }

  /**
   * 編集情報をリクエストの型に変換する
   * @param infos - リクエスト
   */
  public static adaptBulkPutBaseBusinessRequestUpdateInfo(infos: Partial<IBusiness>): BulkPutBaseBusinessRequestUpdateInfos {
    return {
      customerNumber: infos.customerNumber,
      customerName: infos.customerName,
      salesDestinationNumber: infos.salesDestinationNumber,
      salesDestinationName: infos.salesDestinationName,
      endUser: infos.endUser,
      salesUnitPrice: infos.salesUnitPrice ? infos.salesUnitPrice.toString() : undefined,
      baseBusinessResponsibleUserIdList:
        infos.baseBusinessResponsibleUsers && infos.baseBusinessResponsibleUsers.length > 0
          ? infos.baseBusinessResponsibleUsers?.map((u) => u.email).filter((uId) => uId != null)
          : undefined,
      businessFreeColumnValues: BulkEditBusinessAdapter.toRequestBusinessFreeColumnValues(infos.businessFreeColumnValues),
    };
  }

  /**
   * レスポンスの自由欄値をUI用の型へ変換する
   * @param values - レスポンスの自由欄値
   */
  private static toBusinessFreeColumnValues(
    values?: {
      columnNo: number;
      value?: string;
      headerName?: string;
      isMulti?: boolean;
      isConfigured?: boolean;
      target?: 'aggregate' | 'base';
    }[]
  ): BusinessFreeColumnValue[] | undefined {
    return values?.map((value) => ({
      columnNo: value.columnNo,
      headerName: value.headerName ?? '',
      isMulti: value.isMulti ?? false,
      isConfigured: value.isConfigured ?? true,
      value: value.value ?? '',
      target: value.target,
    }));
  }

  /**
   * UI用の自由欄値をリクエストの型へ変換する
   * @param values - UI用の自由欄値
   */
  private static toRequestBusinessFreeColumnValues(values?: BusinessFreeColumnValue[]): { columnNo: number; value: string }[] | undefined {
    return values?.map((column) => ({
      columnNo: column.columnNo,
      value: column.value,
    }));
  }
}
